import { useMemo, useState, useCallback } from 'react';
import EmptyStateNotice from '@/components/EmptyStateNotice';
import { VscTelescope } from '@/components/react-icons/main';
import TableHeader from './pageTable/TableHeader.jsx';
import TableRow from './pageTable/TableRow.jsx';
import Page from './Page.jsx';

const PageTable = ({ columns = [], data = [], sections = [], actions = [], actionMenuFitContent = false, onChange, onActionClick, onRowClick, isLoading, pagination, select = {}, noDataTitle, noDataDescription }) => {
  const [sortConfig, setSortConfig] = useState(null);
  const hasActions = typeof actions === 'function' || actions.length > 0;

  const handleSort = (key) => {
    if (!sortConfig) {
      setSortConfig({ key, direction: 'ascending' });
    } else if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      setSortConfig({ key, direction: 'descending' });
    } else {
      setSortConfig(null);
    }
  };

  const sortData = useCallback(
    (dataToSort) => {
      if (!sortConfig) return dataToSort;
      return [...dataToSort].sort((a, b) => {
        const getSortValue = (item) => {
          const value = item[sortConfig.key];

          if (!value || typeof value !== 'object') {
            return value;
          }

          if ('label' in value && value.label != null) {
            return value.label;
          }

          if ('value' in value) {
            return value.value;
          }

          return value;
        };

        const aValue = getSortValue(a);
        const bValue = getSortValue(b);

        if (aValue == null && bValue == null) {
          return 0;
        }
        if (aValue == null) {
          return 1;
        }
        if (bValue == null) {
          return -1;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    },
    [sortConfig]
  );

  const sortedData = useMemo(() => {
    return sortData(data);
  }, [data, sortData]);

  const sortedSections = useMemo(() => {
    return sections.map((section) => ({
      ...section,
      data: sortData(section.data || [])
    }));
  }, [sections, sortData]);

  const totalDataLength = sections.length > 0 ? sortedSections.reduce((sum, section) => sum + (section.data?.length || 0), 0) : data.length;

  const renderSectionHeader = (title) => {
    return (
      <div className="table-section-header" style={{ width: '100%' }}>
        <div className="table-section-header-content">{title}</div>
      </div>
    );
  };

  return (
    <Page.Section header={<TableHeader columns={columns} onSort={handleSort} sortConfig={sortConfig} hasActions={hasActions} />} className={'comp-page-table'} pagination={pagination}>
      {!isLoading && sections.length > 0 && (
        <>
          {sortedSections.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              {section.title && renderSectionHeader(section.title)}
              {section.data?.map((rowData, index) => (
                <TableRow key={rowData.id || `${sectionIndex}-${index}`} data={rowData} columns={columns} actions={typeof actions === 'function' ? actions(rowData, index) : actions} actionMenuFitContent={actionMenuFitContent} hasActions={hasActions} onChange={onChange} onAction={onActionClick} onRowClick={onRowClick} select={select} />
              ))}
            </div>
          ))}
        </>
      )}
      {!isLoading && sections.length === 0 && pagination?.paginatedData?.length > 0 && (
        <>
          {pagination?.paginatedData?.map((data, index) => (
            <TableRow key={data.id || index} data={data} columns={columns} actions={typeof actions === 'function' ? actions(data, index) : actions} actionMenuFitContent={actionMenuFitContent} hasActions={hasActions} onChange={onChange} onAction={onActionClick} onRowClick={onRowClick} select={select} />
          ))}
        </>
      )}
      {!isLoading && sections.length === 0 && !pagination?.paginatedData?.length && sortedData.map((data, index) => <TableRow key={data.id || index} data={data} columns={columns} actions={typeof actions === 'function' ? actions(data, index) : actions} actionMenuFitContent={actionMenuFitContent} hasActions={hasActions} onChange={onChange} onAction={onActionClick} onRowClick={onRowClick} select={select} />)}
      {isLoading && totalDataLength === 0 && <EmptyStateNotice className="workflow" size={'sm'} loading card={true} title={'Loading data...'} />}
      {!isLoading && totalDataLength === 0 && <EmptyStateNotice className="workflow" size={'sm'} icon={VscTelescope} loading={false} card={true} title={noDataTitle || 'No data yet'} description={noDataDescription || 'Create data to get started'} />}
      <Page.TableKeyHandler row={'.table-row'} />
    </Page.Section>
  );
};

export default PageTable;
